"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Home, PieChart, Building2, Settings, LogOut, Wallet, Tag, Landmark, List, TrendingUp, PlusCircle, ShieldAlert, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { getCurrentUser, getFamilyConfig } from "@/app/actions";
import { logout } from "@/app/(auth)/actions";
import { stopImpersonating } from "@/app/admin/actions";

export default function Sidebar() {
    const pathname = usePathname();
    const router = useRouter();
    const [user, setUser] = useState<{ displayName: string, isBlocked?: boolean, isAdminImpersonating?: boolean } | null>(null);
    const [config, setConfig] = useState<{ enabledModules: string[], isBlocked?: boolean } | null>(null);

    useEffect(() => {
        getCurrentUser().then((u: any) => {
            if (u?.isBlocked) {
                logout().then(() => router.push("/login?error=account_blocked"));
            } else {
                setUser(u);
            }
        });
        getFamilyConfig().then((c: any) => {
            if (c?.isBlocked) {
                logout().then(() => router.push("/login?error=family_blocked"));
            } else {
                setConfig(c);
            }
        });
    }, [router]);

    const isAuthPage = pathname?.startsWith("/login") || pathname?.startsWith("/register") || pathname?.startsWith("/onboarding") || pathname?.startsWith("/admin");
    if (isAuthPage) return null;

    const handleLogout = async () => {
        await logout();
    };

    const handleStopImpersonating = async () => {
        await stopImpersonating();
        router.push("/admin");
    };

    const links = [
        { href: "/", label: "Dashboard", icon: Home },
        { href: "/transactions", label: "Transactions", icon: List },
        { href: "/accounts", label: "Accounts", icon: Landmark },
        { href: "/budget", label: "Budgeting", icon: PieChart },
        { href: "/properties", label: "Properties", icon: Building2, module: "properties" },
        { href: "/investments", label: "Investments", icon: TrendingUp, module: "investments" },
        { href: "/categories", label: "Categories", icon: Tag },
        { href: "/settings", label: "Settings", icon: Settings },
    ].filter(link => !link.module || config?.enabledModules?.includes(link.module));

    const userInitials = user?.displayName
        ? user.displayName.split(" ").map(n => n[0]).join("").toUpperCase()
        : "??";

    return (
        <>
            {user?.isAdminImpersonating && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] bg-danger text-white px-4 py-2 rounded-full shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-10">
                    <ShieldAlert className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Impersonation Mode Active</span>
                    <button 
                        onClick={handleStopImpersonating}
                        className="ml-2 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs font-bold transition-colors"
                    >
                        Return to Admin
                    </button>
                </div>
            )}
            <aside className="w-64 border-r border-border bg-card/50 backdrop-blur-xl hidden md:flex flex-col h-screen fixed top-0 left-0 z-40">
                <div className="p-6">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/30">
                        <Wallet className="w-4 h-4" />
                    </div>
                    <span className="font-bold text-xl tracking-tight">FamilyVault</span>
                </div>
            </div>

            <div className="flex-1 overflow-auto py-6">
                <nav className="grid gap-2 px-4 text-xs font-bold uppercase tracking-wider text-secondary-foreground/40 mb-2 px-7">Main Menu</nav>
                <nav className="grid gap-1 px-4 mb-8">
                    {links.map((item) => (
                        <Link
                            key={item.label}
                            href={item.href}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${pathname === item.href
                                ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                                : "text-secondary-foreground hover:bg-secondary/80 hover:text-foreground"
                                }`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium text-sm">{item.label}</span>
                        </Link>
                    ))}
                </nav>

            </div>

            <div className="p-6 border-t border-border mt-auto">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center border-2 border-primary/20 shadow-sm">
                            <span className="font-bold text-sm">{userInitials}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold truncate max-w-[100px]">{user?.displayName || "Guest User"}</span>
                            <span className="text-xs text-secondary-foreground">Family Member</span>
                        </div>
                    </div>
                    <button 
                        onClick={handleLogout}
                        className="p-2 text-secondary-foreground hover:text-danger hover:bg-danger/10 rounded-xl transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
        </>
    );
}
